import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface Sub {
  id: string;
  user_name: string;
  user_email: string;
  product_name: string;
  pricing_type: string;
  status: string;
  requests_used: number;
  requests_limit: number;
  end_date: string | null;
  created_at: string;
}

const subStatusMap: Record<string, string> = {
  active: 'bg-green-50 text-green-600',
  expired: 'bg-red-50 text-red-500',
  cancelled: 'bg-gray-100 text-gray-500',
  pending: 'bg-amber-50 text-amber-600',
};

function downloadCSV(subs: Sub[]) {
  const headers = ['User', 'Email', 'Product', 'Plan', 'Status', 'Requests Used', 'Requests Limit', 'Expires', 'Created'];
  const rows = subs.map(s => [
    s.user_name, s.user_email, s.product_name,
    s.pricing_type.replace(/_/g, '-'), s.status,
    s.requests_used, s.requests_limit,
    s.end_date ? new Date(s.end_date).toLocaleDateString() : 'Lifetime',
    new Date(s.created_at).toLocaleDateString(),
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `subscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function isExpiringSoon(endDate: string | null) {
  if (!endDate) return false;
  const diff = new Date(endDate).getTime() - Date.now();
  return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000;
}

export default function SubscriptionsTab() {
  const [subs, setSubs] = useState<Sub[]>([]);
  const [loading, setLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState<string | null>(null);
  const [activationLoading, setActivationLoading] = useState<string | null>(null);
  const [reminderMsg, setReminderMsg] = useState<{ id: string; type: 'success' | 'error'; text: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired' | 'cancelled'>('all');
  const [search, setSearch] = useState('');

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('subscriptions')
      .select('id, user_name, user_email, product_name, pricing_type, status, requests_used, requests_limit, end_date, created_at')
      .order('created_at', { ascending: false });
    setSubs(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const sendActivationEmail = async (sub: Sub) => {
    setActivationLoading(sub.id);
    setReminderMsg(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/subscription-activated-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_email: sub.user_email,
          username: sub.user_name,
          product_name: sub.product_name,
          pricing_type: sub.pricing_type,
          end_date: sub.end_date,
        }),
      });
      const result = await res.json();
      if (result.success) {
        setReminderMsg({ id: sub.id, type: 'success', text: result.skipped ? 'SMTP not configured yet.' : `Activation email sent to ${sub.user_email}!` });
      } else {
        setReminderMsg({ id: sub.id, type: 'error', text: result.error || 'Failed to send' });
      }
    } catch {
      setReminderMsg({ id: sub.id, type: 'error', text: 'Network error' });
    }
    setActivationLoading(null);
    setTimeout(() => setReminderMsg(null), 5000);
  };

  const sendRenewalReminder = async (subId: string) => {
    setReminderLoading(subId);
    setReminderMsg(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send_renewal_reminder', subscription_id: subId }),
      });
      const result = await res.json();
      if (result.success) {
        setReminderMsg({ id: subId, type: 'success', text: result.message || 'Reminder sent!' });
      } else {
        setReminderMsg({ id: subId, type: 'error', text: result.error || 'Failed to send reminder' });
      }
    } catch {
      setReminderMsg({ id: subId, type: 'error', text: 'Network error' });
    }
    setReminderLoading(null);
    setTimeout(() => setReminderMsg(null), 4000);
  };

  const expiringSoon = subs.filter(s => s.status === 'active' && isExpiringSoon(s.end_date));

  const filtered = subs.filter(s =>
    (statusFilter === 'all' || s.status === statusFilter) &&
    (!search || s.user_name.toLowerCase().includes(search.toLowerCase()) || s.user_email.toLowerCase().includes(search.toLowerCase()) || s.product_name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Expiry warning banner */}
      {expiringSoon.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-alarm-warning-line text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              {expiringSoon.length} subscription{expiringSoon.length !== 1 ? 's' : ''} expiring within 7 days
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {expiringSoon.map(s => (
                <span key={s.id} className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {s.user_name} — {s.product_name} ({s.end_date ? new Date(s.end_date).toLocaleDateString() : ''})
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active', value: subs.filter(s => s.status === 'active').length, color: 'text-green-600 bg-green-50', icon: 'ri-checkbox-circle-line' },
          { label: 'Expired', value: subs.filter(s => s.status === 'expired').length, color: 'text-red-500 bg-red-50', icon: 'ri-close-circle-line' },
          { label: 'Lifetime', value: subs.filter(s => s.pricing_type === 'lifetime').length, color: 'text-brand-500 bg-brand-50', icon: 'ri-infinity-line' },
          { label: 'Expiring 7d', value: expiringSoon.length, color: 'text-amber-500 bg-amber-50', icon: 'ri-alarm-warning-line' },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="bg-white rounded-xl p-4">
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${s.color}`}><i className={`${s.icon} text-base`} /></span>
            <div className="text-2xl font-bold text-gray-900">{loading ? '—' : s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Reminder toast */}
      {reminderMsg && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${reminderMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
          <i className={reminderMsg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
          {reminderMsg.text}
        </motion.div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" placeholder="Search user or product…" value={search} onChange={e => setSearch(e.target.value)} className="w-full text-sm pl-8 pr-3 py-2 border border-surface-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          <div className="flex items-center gap-1 bg-white border border-surface-3 rounded-lg p-1">
            {(['all', 'active', 'expired', 'cancelled'] as const).map(f => (
              <button key={f} onClick={() => setStatusFilter(f)} className={`px-3 py-1 text-xs font-medium rounded-md capitalize cursor-pointer whitespace-nowrap transition-colors ${statusFilter === f ? 'bg-sidebar text-white' : 'text-gray-500 hover:bg-surface-2'}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{filtered.length} of {subs.length}</span>
          <button onClick={() => downloadCSV(filtered)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-surface-3 rounded-lg text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-download-2-line" />CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 border-b border-surface-3">
              {['User', 'Product', 'Plan', 'Usage', 'Status', 'Expires', 'Actions'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-surface-3 animate-pulse">
                  {Array.from({ length: 7 }).map((__, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded" /></td>)}
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-gray-400">No subscriptions found</td></tr>
            ) : filtered.map((s, i) => {
              const expiring = isExpiringSoon(s.end_date);
              return (
                <motion.tr key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.04 }} className={`border-b border-surface-3 last:border-0 hover:bg-surface-2 transition-colors ${expiring ? 'bg-amber-50/30' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="text-xs font-medium text-gray-800">{s.user_name}</div>
                    <div className="text-[11px] text-gray-400">{s.user_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{s.product_name}</td>
                  <td className="px-4 py-3"><span className="text-[11px] bg-surface-2 text-gray-500 px-2 py-0.5 rounded-full capitalize">{s.pricing_type.replace(/_/g, '-')}</span></td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-700">{s.requests_used.toLocaleString()} / {s.requests_limit.toLocaleString()}</div>
                    <div className="w-24 h-1.5 bg-surface-3 rounded-full mt-1">
                      <div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.min(100, (s.requests_used / s.requests_limit) * 100)}%` }} />
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${subStatusMap[s.status] ?? 'bg-gray-100 text-gray-500'}`}>{s.status}</span></td>
                  <td className="px-4 py-3">
                    <div className={`text-xs ${expiring ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                      {expiring && <i className="ri-alarm-warning-line mr-1" />}
                      {s.end_date ? new Date(s.end_date).toLocaleDateString() : 'Lifetime'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        onClick={() => sendActivationEmail(s)}
                        disabled={activationLoading === s.id}
                        title="Resend activation email"
                        className="flex items-center gap-1 text-[11px] px-2 py-1 border border-green-200 text-green-600 rounded-lg hover:bg-green-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                      >
                        {activationLoading === s.id ? <i className="ri-loader-4-line animate-spin" /> : <><i className="ri-mail-check-line" />Activation</>}
                      </button>
                      {s.status === 'active' && (
                        <button onClick={() => sendRenewalReminder(s.id)} disabled={reminderLoading === s.id} title="Send renewal reminder email" className="flex items-center gap-1 text-[11px] px-2 py-1 border border-amber-200 text-amber-600 rounded-lg hover:bg-amber-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                          {reminderLoading === s.id ? <i className="ri-loader-4-line animate-spin" /> : <><i className="ri-mail-send-line" />Remind</>}
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
